import L from "leaflet";

export function createDrawService({ events, ui, map }) {
  const drawGroup = L.featureGroup().addTo(map.instance);
  const committedByMode = new Map();
  let session = null;

  function startDraw(mode) {
    if (mode !== "aoi" && mode !== "centerline") {
      throw new Error(`Unsupported draw mode: ${mode}`);
    }

    stopDraw("replaced");

    const layerToReplace = committedByMode.get(mode);
    if (layerToReplace) {
      drawGroup.removeLayer(layerToReplace);
      committedByMode.delete(mode);
    }

    const points = [];
    const tempLine = L.polyline([], { color: "#59a6ff", weight: 2, dashArray: "6 6" }).addTo(drawGroup);
    const tempFill =
      mode === "aoi"
        ? L.polygon([], { color: "#59a6ff", weight: 2, fillColor: "#59a6ff", fillOpacity: 0.16 })
            .addTo(drawGroup)
        : null;

    const onClick = (e) => {
      points.push(e.latlng);
      updatePreview();
      events.emit("draw:updated", { mode, pointCount: points.length });
    };

    const onMouseMove = (e) => {
      updatePreview(e.latlng);
    };

    const onDblClick = (e) => {
      L.DomEvent.stop(e);
      finishDraw();
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") stopDraw("cancelled");
    };

    const onContextMenu = (e) => {
      L.DomEvent.stop(e);
      finishDraw();
    };

    const updatePreview = (cursor) => {
      if (points.length === 0) {
        tempLine.setLatLngs([]);
        tempFill?.setLatLngs([]);
        return;
      }
      const path = cursor ? [...points, cursor] : [...points];
      tempLine.setLatLngs(path);
      if (mode === "aoi" && points.length >= 2) tempFill?.setLatLngs(path);
    };

    const finishDraw = () => {
      if (!session) return;
      const minPts = mode === "aoi" ? 3 : 2;
      if (points.length < minPts) {
        ui.toast(`Need at least ${minPts} points for ${mode}`);
        return;
      }

      const finalLayer =
        mode === "aoi"
          ? L.polygon(points, { color: "#59a6ff", weight: 2, fillColor: "#59a6ff", fillOpacity: 0.12 })
          : L.polyline(points, { color: "#59a6ff", weight: 3 });
      finalLayer.addTo(drawGroup);
      committedByMode.set(mode, finalLayer);

      const geometry = toGeoJsonGeometry(mode, points);
      stopDraw("completed", { mode, geometry });
      events.emit("draw:completed", { mode, geometry });
      ui.toast(`${mode === "aoi" ? "AOI" : "Centerline"} captured`);
    };

    map.instance.doubleClickZoom.disable();
    map.instance.on("click", onClick);
    map.instance.on("mousemove", onMouseMove);
    map.instance.on("dblclick", onDblClick);
    map.instance.on("contextmenu", onContextMenu);
    document.addEventListener("keydown", onKeyDown);

    session = {
      mode,
      tempLine,
      tempFill,
      off() {
        map.instance.off("click", onClick);
        map.instance.off("mousemove", onMouseMove);
        map.instance.off("dblclick", onDblClick);
        map.instance.off("contextmenu", onContextMenu);
        document.removeEventListener("keydown", onKeyDown);
        setTimeout(() => map.instance.doubleClickZoom.enable(), 0);
      },
    };

    events.emit("draw:started", { mode });
    ui.toast(`Drawing ${mode}. Click to add points, double-click or right-click to finish, Esc to cancel.`);
  }

  function stopDraw(reason = "stopped", payload = null) {
    if (!session) return;
    const { mode, tempLine, tempFill, off } = session;
    off();
    drawGroup.removeLayer(tempLine);
    if (tempFill) drawGroup.removeLayer(tempFill);
    session = null;
    events.emit("draw:stopped", { mode, reason, ...(payload ?? {}) });
  }

  return { startDraw, stopDraw };
}

function toGeoJsonGeometry(mode, points) {
  const coords = points.map((p) => [p.lng, p.lat]);
  if (mode === "centerline") {
    return { type: "LineString", coordinates: coords };
  }
  const ring = [...coords, coords[0]];
  return { type: "Polygon", coordinates: [ring] };
}
